// [Project] > [Status] > [Building readiness]: whether the building can take the
// installation, across its structural, roof condition and on-site survey areas.
// See docs/Y2026-Q3/design-building-readiness-status.md.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import {
  AREA_LABEL,
  AREA_ORDER,
  type AreaKey,
  type BuildingReadiness,
  type ReadinessArea,
  type ReadinessConstraint,
  type ReadinessDecision,
  type ReadinessEvidence,
  type ReadinessObservation,
  type TimelineEvent,
} from "../api/hooks/buildingReadiness";
import type { PortfolioRow } from "../api/hooks/useAgentPortfolio";
import {
  StatusForCard,
  VerdictReason,
  resolveVerdictItem,
} from "./PortfolioRowParts";
import { StatusCardChips } from "./StatusCardParts";
import type {
  AgentLists,
  NextAction,
  ProjectAgentStatusList,
} from "../api/hooks/useProjectAgentStatus";
import { DateTable } from "./TrackedDateTable";
import { Fold } from "./Fold";
import { ReadyToBuildBody, ReadyToBuildCount } from "./ReadyToBuild";
import { RoofHistoryCard } from "./RoofHistoryCard";
import { useReadyToBuildOpen } from "./useReadyToBuildOpen";
import { StructuralHistoryCard } from "./StructuralHistoryCard";
import { SurveyHistoryCard } from "./SurveyHistoryCard";
import { WarrantyDocuments } from "./WarrantyDocuments";
import { projectSource } from "../utils/nextActions";
import { ActionTable } from "./NextActionTable";
import { formatDay } from "../utils/attributeFormat";
import { hashId } from "../utils/useScrollToHash";
import { AREA_LINE_LABELS, areaSummaryText } from "../utils/readinessAreas";
import { sourceCitation, useReadinessCitations } from "./useReadinessCitations";
import styles from "./BuildingReadiness.module.css";
import { Chevron } from "./Chevron";
import { UPLOAD_ID } from "../utils/uploadId";

type Cite = (text: string) => ReactNode;

// An entry's own source, after its text: the file's name, opening at the quote.
const source = (
  cite: Cite,
  entry: { upload_id: string; quote?: string },
): ReactNode =>
  entry.upload_id ? (
    <> {cite(sourceCitation(entry.upload_id, entry.quote))}</>
  ) : null;

const DECISION_LABEL: Record<string, string> = {
  accept_as_is: "Accept as is",
  accept_with_limits: "Accept with limits",
  remediate: "Remediate",
  dont_proceed: "Don't proceed",
  proceed: "Proceed",
  proceed_with_conditions: "Proceed with conditions",
};

// Each area's proof goes by the name an operator would give it.
const EVIDENCE_TITLE: Record<AreaKey, string> = {
  structural: "Report versions",
  roof_condition: "Quotes and evidence",
  on_site_survey: "Surveys held, and where they disagree",
};

const ADHERENCE_LABEL: Record<ReadinessConstraint["adherence"], string> = {
  meets: "Meets",
  deviates: "Deviates",
  not_evidenced: "Not evidenced",
  conflict: "Conflict",
  open: "Open",
};

const ADHERENCE_TONE: Record<ReadinessConstraint["adherence"], string> = {
  meets: styles.pillGood,
  deviates: styles.pillBad,
  not_evidenced: styles.pillMuted,
  conflict: styles.pillWarn,
  open: styles.pillWarn,
};

const STANDING_LABEL: Record<ReadinessEvidence["standing"], string> = {
  current: "Current",
  held: "Held",
  superseded: "Superseded",
  not_received: "Not received",
  email_only: "Email attachment only",
  conflict: "Conflict",
};

const STANDING_TONE: Record<ReadinessEvidence["standing"], string> = {
  current: styles.pillInfo,
  held: styles.pillGood,
  superseded: styles.pillMuted,
  not_received: styles.pillWarn,
  email_only: styles.pillWarn,
  conflict: styles.pillWarn,
};

const ORIGIN_LABEL: Record<Exclude<ReadinessEvidence["origin"], "">, string> = {
  commissioned: "Commissioned",
  bma_form: "BMA form",
  out_of_band: "Out of band",
  received: "Received",
};

const OBSERVATION_TONE: Record<ReadinessObservation["state"], string> = {
  open: styles.pillBad,
  resolved: styles.pillGood,
  info: styles.pillMuted,
};

const OBSERVATION_LABEL: Record<ReadinessObservation["state"], string> = {
  open: "Open",
  resolved: "Resolved",
  info: "Info only",
};

// The decision's terms, who took it and when, shown on hover over its pill. A hover is
// plain text, so a citation in the note is dropped; its source links beside the pill.
const DECISION_CITATION = new RegExp(String.raw`\s*\[${UPLOAD_ID}(?:\s+"[^"]*")?\]`, "g");
const decisionDetail = (decision: ReadinessDecision): string | undefined =>
  [
    decision.note.replace(DECISION_CITATION, ""),
    decision.by,
    decision.date ? formatDay(decision.date) : "",
  ]
    .filter(Boolean)
    .join(" · ") || undefined;

const decisionPill = (decision: ReadinessDecision): ReactNode => {
  const outcome = DECISION_LABEL[decision.outcome] ?? decision.outcome;
  const title = decisionDetail(decision);
  if (decision.state === "taken") {
    return (
      <span className={styles.pillInfo} title={title}>
        Decided: {outcome.toLowerCase()}
      </span>
    );
  }
  if (decision.state === "likely") {
    return (
      <span className={styles.pillWarn} title={title}>
        Likely outcome: {outcome.toLowerCase()}
      </span>
    );
  }
  return (
    <span className={styles.pillWarn} title={title}>
      Decision pending
    </span>
  );
};

// The agent writes an area's status one point per line; a status stored before that
// is one paragraph and stays one.
const AreaStatus = ({ text, cite }: { text: string; cite: Cite }) => {
  const points = text
    .split("\n")
    .map((line) => line.replace(/^\s*(?:[-*•]\s+)?/, "").trim())
    .filter(Boolean);
  if (points.length === 1)
    return <p className={styles.prose}>{cite(points[0])}</p>;
  return (
    <ul className={styles.bullets}>
      {points.map((point, i) => (
        <li key={i}>{cite(point)}</li>
      ))}
    </ul>
  );
};

// A player's people arrive as one string, "A (role), B (firm)": one person per line,
// splitting only on commas outside brackets so a person's own note stays whole.
const splitPeople = (people: string): string[] => {
  const out: string[] = [];
  let depth = 0;
  let current = "";
  for (const ch of people) {
    if (ch === "(") depth += 1;
    if (ch === ")") depth = Math.max(0, depth - 1);
    if ((ch === "," || ch === ";") && depth === 0) {
      out.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  out.push(current);
  return out.map((p) => p.trim()).filter(Boolean);
};

const Collapsible = ({
  title,
  count,
  children,
}: {
  title: string;
  count: string;
  children: ReactNode;
}) => (
  <details className={styles.section}>
    <summary className={styles.sectionSummary}>
      <span className={styles.caret}>
        <Chevron tree />
      </span>
      <span className={styles.sectionLabel}>{title}</span>
      <span className={styles.small}>{count}</span>
    </summary>
    <div className={styles.sectionBody}>{children}</div>
  </details>
);

const Constraints = ({
  constraints,
  cite,
}: {
  constraints: ReadinessConstraint[];
  cite: Cite;
}) => (
  <div className={styles.tableWrap}>
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Constraint</th>
          <th>Set by</th>
          <th>Adherence</th>
          <th>Who accepts a deviation</th>
          <th>Evidence</th>
        </tr>
      </thead>
      <tbody>
        {constraints.map((c, i) => (
          <tr key={`${c.requirement}-${i}`}>
            <td>{cite(c.requirement)}</td>
            <td>{c.set_by || "-"}</td>
            <td>
              <span className={ADHERENCE_TONE[c.adherence]}>
                {c.label || ADHERENCE_LABEL[c.adherence]}
              </span>
            </td>
            <td>{c.accepts_deviation || "-"}</td>
            <td>
              {c.evidence ? cite(c.evidence) : "-"}
              {source(cite, c)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Observations = ({
  observations,
  cite,
}: {
  observations: ReadinessObservation[];
  cite: Cite;
}) => (
  <div className={styles.tableWrap}>
    <table className={styles.table}>
      <thead>
        <tr>
          <th>#</th>
          <th>Raised</th>
          <th>Observation</th>
          <th>Sits with</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {observations.map((o, i) => (
          <tr key={`${o.text}-${i}`}>
            <td>{i + 1}</td>
            <td>{o.raised || "-"}</td>
            <td>{cite(o.text)}</td>
            <td>{o.sits_with || "-"}</td>
            <td>
              <span className={OBSERVATION_TONE[o.state]}>
                {o.label || OBSERVATION_LABEL[o.state]}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// The agent appends events in whatever order it learns of them, so sort by date rather
// than reverse; a same-day pair keeps the later-written one on top.
const newestFirst = (events: TimelineEvent[]): TimelineEvent[] =>
  [...events].reverse().sort((a, b) => b.date.localeCompare(a.date));

const AreaCard = ({
  areaKey,
  area,
  cite,
  actions,
  dates,
  history,
  documents,
}: {
  areaKey: AreaKey;
  area: ReadinessArea | undefined;
  cite: Cite;
  /** The area's next actions and tracked dates. */
  actions: ReactNode;
  dates: ReactNode | false;
  /** The area's own history, shown in place of its timeline and evidence. */
  history: ReactNode;
  /** The roof's documents and warranties status, on an Amazon building; null elsewhere. */
  documents: ReactNode;
}) => {
  const label = AREA_LABEL[areaKey];
  const lists = (
    <>
      {documents}
      <div className={styles.blockRule}>
        <h4 className={styles.label}>Next actions - {label.toLowerCase()}</h4>
        {actions}
      </div>
      {dates && (
        <div className={styles.blockRule}>
          <h4 className={styles.label}>
            Tracked dates - {label.toLowerCase()}
          </h4>
          {dates}
        </div>
      )}
    </>
  );
  if (!area) {
    return (
      <section className={styles.card}>
        <header className={styles.cardHead}>
          <h3>{label}</h3>
        </header>
        <div className={styles.block}>
          <p className={styles.muted}>Nothing recorded for this area yet.</p>
        </div>
        {history}
        {lists}
      </section>
    );
  }
  const openObservations = area.observations.filter(
    (o) => o.state === "open",
  ).length;
  return (
    <section className={styles.card}>
      <header className={styles.cardHead}>
        <h3>{label}</h3>
        {area.stage && <span className={styles.stage}>{area.stage}</span>}
        {decisionPill(area.decision)}
        {source(cite, area.decision)}
      </header>
      <div className={styles.story}>
        <div className={styles.block}>
          <h4 className={styles.label}>Status</h4>
          {area.progress.length > 0 && (
            <div className={styles.flow}>
              {area.progress.map((p, i) => (
                <span key={`${p.label}-${i}`} className={styles.flowItem}>
                  {i > 0 && (
                    <span className={styles.arrow} aria-hidden="true">
                      →
                    </span>
                  )}
                  <span className={styles[`flow_${p.state}`]}>{p.label}</span>
                </span>
              ))}
            </div>
          )}
          {area.status.trim() ? (
            <AreaStatus text={area.status} cite={cite} />
          ) : (
            <p className={styles.muted}>No status written yet.</p>
          )}
        </div>
        <div className={styles.block}>
          <h4 className={styles.label}>Players</h4>
          {area.players.length === 0 ? (
            <p className={styles.muted}>None recorded.</p>
          ) : (
            <ul className={styles.players}>
              {area.players.map((p, i) => (
                <li key={`${p.name}-${i}`} className={styles.player}>
                  <div className={styles.playerName}>{p.name}</div>
                  {p.role && <div className={styles.playerRole}>{p.role}</div>}
                  {splitPeople(p.people).map((person, j) => (
                    <div key={`${person}-${j}`} className={styles.playerPerson}>
                      {person}
                    </div>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {lists}
      {area.constraints.length > 0 && (
        <Collapsible title="Constraints" count={`${area.constraints.length}`}>
          <Constraints constraints={area.constraints} cite={cite} />
        </Collapsible>
      )}
      {area.decision.outcome === "remediate" && (
        <div className={styles.blockRule}>
          <h4 className={styles.label}>Items to put right</h4>
          <p className={styles.prose}>
            {area.remediation_items.length > 0 && (
              <>
                {area.remediation_items.map((item, i) => (
                  <span key={`${item}-${i}`}>
                    {i > 0 && ", "}
                    {cite(item)}
                  </span>
                ))}
                .{" "}
              </>
            )}
            Their status, owners and dates are in this area&apos;s next actions,
            and what has happened is in the timeline.
          </p>
        </div>
      )}
      {area.observations.length > 0 && (
        <Collapsible
          title="Observations collected"
          count={`${area.observations.length} · ${openObservations} open`}
        >
          <Observations observations={area.observations} cite={cite} />
        </Collapsible>
      )}
      {history ?? (
        <div className={styles.grid2Rule}>
          <div>
            <Collapsible
              title="Timeline"
              count={`${area.timeline.length} events`}
            >
              {area.timeline.length === 0 ? (
                <p className={styles.muted}>No events recorded.</p>
              ) : (
                <ol className={styles.timeline}>
                  {newestFirst(area.timeline).map((e, i) => (
                    <li key={`${e.date}-${i}`}>
                      <span className={styles.when}>
                        {formatDay(e.date)}
                        {e.until && ` - ${formatDay(e.until)}`}
                      </span>
                      <span>
                        {cite(e.what)}
                        {source(cite, e)}
                        {e.source && (
                          <span className={styles.src}>{cite(e.source)}</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </Collapsible>
          </div>
          <div>
            <Collapsible
              title={EVIDENCE_TITLE[areaKey]}
              count={`${area.evidence.length}`}
            >
              {area.evidence.length === 0 ? (
                <p className={styles.muted}>Nothing recorded.</p>
              ) : (
                <ul className={styles.list}>
                  {area.evidence.map((e, i) => (
                    <li key={`${e.what}-${i}`}>
                      <span>
                        {e.origin && (
                          <span className={styles.origin}>
                            {ORIGIN_LABEL[e.origin]}
                          </span>
                        )}
                        {cite(e.what)}
                        {source(cite, e)}
                        {e.detail && (
                          <span className={styles.src}>{cite(e.detail)}</span>
                        )}
                      </span>
                      <span className={STANDING_TONE[e.standing]}>
                        {e.label || STANDING_LABEL[e.standing]}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Collapsible>
          </div>
        </div>
      )}
    </section>
  );
};

const isArea = (area: string): area is AreaKey =>
  (AREA_ORDER as readonly string[]).includes(area);

// Which actions each table shows; module-level so the table's memo holds across renders.
const outsideAreas = (action: NextAction): boolean => !isArea(action.area);
const inArea = Object.fromEntries(
  AREA_ORDER.map((key) => [key, (action: NextAction) => action.area === key]),
) as Record<AreaKey, (action: NextAction) => boolean>;

/** The area holding the action or tracked date a link's hash names, if any. */
const areaOfItem = (
  lists: AgentLists | null | undefined,
  hash: string,
): AreaKey | undefined => {
  if (!hash || !lists) return undefined;
  const id = hashId(hash);
  const area =
    lists.actions.find((a) => a.action_id === id)?.area ??
    lists.tracked_dates.find((d) => d.date_id === id)?.area;
  return area !== undefined && isArea(area) ? area : undefined;
};

const HISTORY_LABEL: Record<AreaKey, string> = {
  structural: "Structural history",
  roof_condition: "Roof history",
  on_site_survey: "Survey history",
};

export const BuildingReadinessView = ({
  row,
  projectId,
  data,
  agent,
}: {
  row: PortfolioRow;
  projectId: string;
  /** The project's next actions, each area's shown inside it. */
  data: ProjectAgentStatusList;
  agent: string;
}) => {
  const mine = data.agents.find((a) => a.agent === agent)?.lists;
  const outside = {
    actions: (mine?.actions ?? []).some((a) => !a.closed && !isArea(a.area)),
    dates: (mine?.tracked_dates ?? []).some(
      (d) => !d.closed && !isArea(d.area),
    ),
  };
  const datesIn = (key: string) =>
    (mine?.tracked_dates ?? []).some((d) => !d.closed && d.area === key);
  const openIn = (key: string): number =>
    (mine?.actions ?? []).filter((a) => !a.closed && a.area === key).length;
  const readiness: BuildingReadiness = row.readiness ?? {
    areas: {},
    next_steps: [],
  };
  const mineEntry = data.agents.find((a) => a.agent === agent);
  const histories = mineEntry?.readiness_histories ?? null;
  const warranty = mineEntry?.warranty_documents ?? null;
  const cited = useMemo(() => [histories, warranty], [histories, warranty]);
  const { cite, modal } = useReadinessCitations(readiness, cited);
  // Only the chosen area renders, so a link to an action or date inside another area
  // has to switch to that area first or its row is never there to land on.
  const { hash } = useLocation();
  const linkedArea = areaOfItem(mine, hash);
  const [selected, setSelected] = useState<AreaKey>(
    linkedArea ?? AREA_ORDER[0],
  );
  useEffect(() => {
    if (linkedArea) setSelected(linkedArea);
  }, [linkedArea]);
  // The Ready to build card starts closed, but opens for a link to it or to an action or
  // date it lists outside the three areas.
  const id = hashId(hash);
  const readyOpen =
    useReadyToBuildOpen() ||
    (Boolean(hash) &&
      !linkedArea &&
      Boolean(
        mine?.actions.some((a) => a.action_id === id) ||
        mine?.tracked_dates.some((d) => d.date_id === id),
      ));

  return (
    <div className={styles.page}>
      {modal}
      <div className={styles.grid2}>
        <section className={styles.card}>
          <header className={styles.cardHead}>
            <h3>Status</h3>
            <StatusCardChips row={row} lists={{ data, agent, projectId }} />
          </header>
          {row.on_track?.verdict && (
            <div className={styles.block}>
              <VerdictReason
                onTrack={row.on_track}
                found={resolveVerdictItem(row.on_track, {
                  data,
                  agent,
                  projectId,
                })}
              />
            </div>
          )}
          <div className={styles.block}>
            {/* Its area lines restate the area cards below, so only the opener shows. */}
            <StatusForCard
              text={row.status_details}
              omitLabels={AREA_LINE_LABELS}
            />
          </div>
        </section>
        <section className={styles.card}>
          <Fold
            headClassName={styles.cardHead}
            open={readyOpen}
            head={
              <>
                <h3>Ready to build</h3>
                <span className={styles.spacer} />
                <ReadyToBuildCount agent={agent} data={data} />
              </>
            }
          >
            <div className={styles.block} id="ready-to-build">
              <ReadyToBuildBody
                agent={agent}
                projectId={projectId}
                data={data}
              />
            </div>
            {outside.actions && (
              <div className={styles.blockRule}>
                <h4 className={styles.label}>
                  Next actions outside the three areas
                </h4>
                <ActionTable
                  scope={{ kind: "agent", agent }}
                  sources={[projectSource(projectId, data)]}
                  include={outsideAreas}
                  empty="None."
                  controls={false}
                  showArea={false}
                  anchored
                />
              </div>
            )}
            {outside.dates && (
              <div className={styles.blockRule}>
                <h4 className={styles.label}>
                  Tracked dates outside the three areas
                </h4>
                <DateTable
                  agent={agent}
                  projectId={projectId}
                  data={data}
                  include={(d) => !isArea(d.area)}
                  empty="None."
                  collapseCovered
                  showArea={false}
                />
              </div>
            )}
          </Fold>
        </section>
      </div>

      <section className={styles.card}>
        <div className={styles.strip} role="tablist" aria-label="Areas">
          {AREA_ORDER.map((key) => {
            const area = readiness.areas[key];
            const active = key === selected;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                id={`area-tab-${key}`}
                aria-selected={active}
                aria-controls={`area-panel-${key}`}
                className={`${styles.stripCell} ${active ? styles.stripCellActive : ""}`}
                onClick={() => setSelected(key)}
              >
                <span className={styles.stripHead}>
                  <strong>{AREA_LABEL[key]}</strong>
                  {area?.stage && (
                    <span className={styles.stage}>{area.stage}</span>
                  )}
                </span>
                <span className={styles.prose}>{areaSummaryText(area)}</span>
                {area && (
                  <span className={styles.small}>
                    {openIn(key)} open action{openIn(key) === 1 ? "" : "s"} ·{" "}
                    {area.constraints.length} constraints ·{" "}
                    {histories
                      ? `${histories[key].entries.filter((e) => e.withdrawn_reason === null).length} exchanges`
                      : `${area.timeline.length} events`}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <div
        role="tabpanel"
        id={`area-panel-${selected}`}
        aria-labelledby={`area-tab-${selected}`}
      >
        <AreaCard
          key={selected}
          areaKey={selected}
          area={readiness.areas[selected]}
          cite={cite}
          actions={
            <ActionTable
              scope={{ kind: "agent", agent }}
              sources={[projectSource(projectId, data)]}
              include={inArea[selected]}
              empty="Nothing left to do in this area."
              controls={false}
              showArea={false}
              anchored
            />
          }
          history={
            // Until the roof or survey history holds an entry, its tab keeps the area's
            // timeline and evidence.
            histories &&
            (selected === "structural" || histories[selected].entries.length > 0) ? (
              <div className={styles.blockRule}>
                <h4 className={styles.label}>{HISTORY_LABEL[selected]}</h4>
                {selected === "structural" ? (
                  <StructuralHistoryCard
                    history={histories.structural}
                    cite={cite}
                    projectId={projectId}
                    editable={data.as_of === null}
                  />
                ) : selected === "roof_condition" ? (
                  <RoofHistoryCard history={histories.roof_condition} cite={cite} />
                ) : (
                  <SurveyHistoryCard history={histories.on_site_survey} cite={cite} />
                )}
              </div>
            ) : null
          }
          documents={
            selected === "roof_condition" && warranty ? (
              <WarrantyDocuments rows={warranty} cite={cite} />
            ) : null
          }
          dates={
            datesIn(selected) && (
              <DateTable
                agent={agent}
                projectId={projectId}
                data={data}
                include={(d) => d.area === selected}
                empty="No tracked dates in this area."
                collapseCovered
                showArea={false}
              />
            )
          }
        />
      </div>
    </div>
  );
};
